import { useState, useEffect, useCallback } from 'react';
import api, { schedule as scheduleApi, auth as authApi, sites as sitesApi,
              absences as absencesApi, scheduleTemplates as templatesApi } from '../utils/api';
import { useAuth } from '../utils/auth';
import { useSocketEvent } from '../utils/socket';
import { format, startOfWeek, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, LogIn, LogOut, Clock, ChevronLeft, ChevronRight,
         Trash2, AlertTriangle, CheckCircle2, XCircle, Edit2,
         CalendarDays, UserX, LayoutTemplate, BarChart2, Briefcase,
         ClipboardList, MonitorSmartphone } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';

// Helper : formater une date sans crasher si invalide
const safeFormat = (val, fmt, opts={}) => {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return '—';
    return format(d, fmt, opts);
  } catch { return '—'; }
};

const TABS = [
  ['planning',     'Planning',     CalendarDays],
  ['absences',     'Absences',     UserX],
  ['templates',    'Templates',    LayoutTemplate],
  ['my-hours',     'Mes heures',   BarChart2],
  ['missions',     'Missions',     Briefcase],
  ['time-entries', 'Pointages',    ClipboardList],
  ['kiosk',        'Kiosque',      MonitorSmartphone],
];

const ABSENCE_LABELS = {
  conge_paye:'Congé payé', rtt:'RTT', maladie:'Arrêt maladie',
  sans_solde:'Sans solde', formation:'Formation', autre:'Autre',
};
const ABSENCE_COLORS = {
  conge_paye:'var(--teal)', rtt:'var(--accent-light)', maladie:'var(--red)',
  sans_solde:'var(--amber)', formation:'var(--green)', autre:'var(--text-2)',
};
const STATUS_BADGE = { pending:'badge-amber', approved:'badge-green', rejected:'badge-red' };
const STATUS_LABEL = { pending:'En attente', approved:'Validé', rejected:'Refusé' };
const DAYS_SHORT = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];

function prevMonth(m) { const d=new Date(m+'-15'); d.setMonth(d.getMonth()-1); return format(d,'yyyy-MM'); }
function nextMonth(m) { const d=new Date(m+'-15'); d.setMonth(d.getMonth()+1); return format(d,'yyyy-MM'); }

// ── Planning semaine ──────────────────────────────────────────
function WeekPlanning({ can, user, sites, absencesByUser }) {
  const [weekStart, setWeekStart]   = useState(startOfWeek(new Date(),{weekStartsOn:1}));
  const [schedules, setSchedules]   = useState([]);
  const [availability, setAvail]    = useState([]);
  const [users, setUsers]           = useState([]);
  const [filterSite, setFilterSite] = useState('');
  const [showAdd, setShowAdd]       = useState(false);
  const [showApply, setShowApply]   = useState(false);
  const [applyWeeks, setApplyWeeks] = useState(1);
  const [templates, setTemplates]   = useState([]);
  const [viewMode, setViewMode]     = useState('week');
  const [newEntry, setNewEntry] = useState({userId:'',workDate:'',startTime:'',endTime:'',siteId:''});

  const weekDays = Array.from({length:7},(_,i)=>addDays(weekStart,i));

  const load = useCallback(async () => {
    const after = format(weekStart,'yyyy-MM-dd');
    try {
      const [s, av] = await Promise.all([
        scheduleApi.list({week:after}),
        scheduleApi.availability({month:format(weekStart,'yyyy-MM')}),
      ]);
      setSchedules(s.data.schedules);
      setAvail(av.data.availabilities);
      if (can('staff')) authApi.users().then(r=>setUsers(r.data.users)).catch(()=>{});
      if (can('manager')) templatesApi.list().then(r=>setTemplates(r.data.templates)).catch(()=>{});
    } catch { toast.error('Erreur chargement planning'); }
  }, [weekStart, can]);

  useEffect(()=>{load();},[load]);
  useSocketEvent('schedule:updated', load);
  useSocketEvent('availability:updated', load);

  const addSchedule = async e => {
    e.preventDefault();
    try {
      await scheduleApi.create(newEntry);
      toast.success('Horaires ajoutés');
      setShowAdd(false); load();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  const delSchedule = async (id, e) => {
    e.stopPropagation();
    try { await scheduleApi.del(id); load(); }
    catch { toast.error('Erreur suppression'); }
  };

  const toggleAvail = async (date, current) => {
    try { await scheduleApi.setAvailability({availDate:date,isAvailable:!current}); load(); }
    catch { toast.error('Erreur'); }
  };

  const applyTemplate = async (templateId, overwrite) => {
    const monday = format(weekStart,'yyyy-MM-dd');
    try {
      const r = await templatesApi.apply(templateId, {weekStart:monday, overwrite, weeks:applyWeeks});
      const msg = `${r.data.created} horaire(s) créé(s) sur ${r.data.weeks} semaine(s)`;
      toast.success(r.data.skipped > 0 ? `${msg}, ${r.data.skipped} ignoré(s)` : msg);
      setShowApply(false); load();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  // Filtrer par site
  const displayedUsers = users.filter(u => {
    if (!filterSite) return true;
    return u.site_id === filterSite;
  });

  const staffUsers     = displayedUsers.filter(u=>u.role!=='freelance');
  const freelanceUsers = displayedUsers.filter(u=>u.role==='freelance');
  const allDisplayed   = [...staffUsers, ...freelanceUsers];

  // Vérifier si un user est absent un jour donné
  const isAbsent = (userId, dateStr) => {
    const userAbsences = absencesByUser?.[userId] || [];
    return userAbsences.find(a =>
      a.status === 'approved' && a.date_from <= dateStr && a.date_to >= dateStr
    );
  };

  return (
    <div>
      {/* Barre d'outils */}
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        <button className="btn btn-secondary btn-sm" onClick={()=>setWeekStart(d=>addDays(d,-7))}>
          <ChevronLeft size={15}/>
        </button>
        <span style={{fontWeight:600,fontSize:15}}>
          {format(weekStart,'d MMM',{locale:fr})} – {format(addDays(weekStart,6),'d MMM yyyy',{locale:fr})}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={()=>setWeekStart(d=>addDays(d,7))}>
          <ChevronRight size={15}/>
        </button>
        <button className="btn btn-ghost btn-sm" onClick={()=>setWeekStart(startOfWeek(new Date(),{weekStartsOn:1}))}>
          Cette semaine
        </button>

        {/* Filtre site */}
        {sites.length > 0 && (
          <select className="input btn-sm" style={{width:'auto',marginLeft:8}} value={filterSite}
            onChange={e=>setFilterSite(e.target.value)}>
            <option value="">Tous les sites</option>
            {sites.map(s=>(
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}

        {/* Toggle vue */}
        <div style={{display:'flex',gap:0,borderRadius:8,overflow:'hidden',border:'1px solid var(--border)',marginLeft:8}}>
          {[['week','Semaine'],['month','Mois']].map(([v,l])=>(
            <button key={v} onClick={()=>setViewMode(v)}
              style={{padding:'5px 12px',border:'none',cursor:'pointer',fontFamily:'inherit',fontSize:12,fontWeight:600,
                background:viewMode===v?'var(--accent)':'var(--bg-elevated)',
                color:viewMode===v?'#fff':'var(--text-3)'}}>
              {l}
            </button>
          ))}
        </div>

        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          {can('manager') && templates.length>0 && (
            <button className="btn btn-secondary btn-sm" onClick={()=>setShowApply(true)}>
              📋 Appliquer un template
            </button>
          )}
          {can('manager') && (
            <button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}>
              <Plus size={14}/> Ajouter
            </button>
          )}
        </div>
      </div>

      {/* Légende sites */}
      {sites.length > 0 && (
        <div style={{display:'flex',gap:10,marginBottom:12,flexWrap:'wrap'}}>
          {sites.map(s=>(
            <div key={s.id} style={{display:'flex',alignItems:'center',gap:5,fontSize:12}}>
              <div style={{width:10,height:10,borderRadius:'50%',background:s.color}}/>
              <span style={{color:'var(--text-2)'}}>{s.name}</span>
            </div>
          ))}
          <div style={{display:'flex',alignItems:'center',gap:5,fontSize:12}}>
            <div style={{width:10,height:10,borderRadius:2,background:'rgba(255,77,109,0.4)'}}/>
            <span style={{color:'var(--text-2)'}}>Absence validée</span>
          </div>
        </div>
      )}

      <div className="card" style={{padding:0,overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',minWidth:700}}>
          <thead>
            <tr style={{borderBottom:'1px solid var(--border)'}}>
              <th style={{width:160,padding:'12px 16px',textAlign:'left',fontSize:12,color:'var(--text-3)',fontWeight:600}}>
                Collaborateur
              </th>
              {weekDays.map(d=>{
                const isToday = format(d,'yyyy-MM-dd')===format(new Date(),'yyyy-MM-dd');
                return (
                  <th key={String(d)} style={{textAlign:'center',padding:'10px 4px',fontSize:12,
                    color:isToday?'var(--accent-light)':'var(--text-3)',fontWeight:600,
                    background:isToday?'rgba(124,92,252,0.06)':'transparent'}}>
                    <div>{format(d,'EEE',{locale:fr})}</div>
                    <div style={{fontSize:14,fontWeight:isToday?700:400,marginTop:2}}>{format(d,'d/MM')}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {allDisplayed.map(u=>{
              const userSite = sites.find(s=>s.id===u.site_id);
              return (
                <tr key={u.id} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{padding:'8px 16px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      {userSite && (
                        <div style={{width:4,height:32,borderRadius:2,background:userSite.color,flexShrink:0}}/>
                      )}
                      <div className="avatar" style={{width:26,height:26,fontSize:10}}>
                        {u.first_name?.[0]}{u.last_name?.[0]}
                      </div>
                      <div style={{minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:12,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {u.first_name} {u.last_name}
                        </div>
                        <div style={{display:'flex',gap:4,marginTop:2,flexWrap:'wrap'}}>
                          <span className={`badge ${u.role==='freelance'?'badge-amber':'badge-purple'}`} style={{fontSize:9}}>{u.role}</span>
                          {userSite && <span style={{fontSize:9,color:userSite.color}}>{userSite.name}</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  {weekDays.map(d=>{
                    const ds = format(d,'yyyy-MM-dd');
                    const isToday = ds===format(new Date(),'yyyy-MM-dd');
                    const absence = isAbsent(u.id, ds);

                    if (absence) {
                      return (
                        <td key={ds} style={{textAlign:'center',padding:'6px 4px',
                          background:isToday?'rgba(124,92,252,0.03)':'transparent'}}>
                          <div style={{fontSize:11,padding:'4px 6px',borderRadius:6,
                            background:'rgba(255,77,109,0.15)',color:'var(--red)',
                            border:'1px solid rgba(255,77,109,0.3)'}}>
                            {ABSENCE_LABELS[absence.type]||'Absent'}
                          </div>
                        </td>
                      );
                    }

                    if (u.role==='freelance') {
                      const av = availability.find(a=>a.user_id===u.id&&(a.avail_date_str||a.avail_date?.slice(0,10))===ds);
                      return (
                        <td key={ds} style={{textAlign:'center',padding:'6px 4px',
                          background:isToday?'rgba(124,92,252,0.03)':'transparent'}}>
                          <div onClick={()=>user.id===u.id&&toggleAvail(ds,av?.is_available)}
                            style={{display:'inline-block',padding:'3px 8px',borderRadius:6,fontSize:11,
                              cursor:user.id===u.id?'pointer':'default',
                              background:av?.is_available?'rgba(34,211,160,0.12)':'rgba(255,255,255,0.03)',
                              color:av?.is_available?'var(--green)':'var(--text-3)',
                              border:`1px solid ${av?.is_available?'rgba(34,211,160,0.3)':'var(--border)'}`}}>
                            {av?.is_available?'✓ Dispo':'—'}
                          </div>
                        </td>
                      );
                    }

                    const sch = schedules.filter(s=>s.user_id===u.id&&(s.work_date_str||s.work_date?.slice(0,10))===ds);
                    return (
                      <td key={ds} style={{textAlign:'center',padding:'4px',
                        background:isToday?'rgba(124,92,252,0.03)':'transparent'}}>
                        {sch.map(s=>{
                          const schSite = sites.find(st=>st.id===s.site_id);
                          const isDiffSite = s.site_id && s.site_id !== u.site_id;
                          return (
                            <div key={s.id} style={{
                              display:'inline-flex',alignItems:'center',gap:3,
                              fontSize:11,padding:'3px 7px',borderRadius:6,marginBottom:2,
                              background: schSite ? `${schSite.color}22` : 'rgba(124,92,252,0.12)',
                              color: schSite?.color || 'var(--accent-light)',
                              border:`1px solid ${schSite?.color+'44' || 'rgba(124,92,252,0.25)'}`,
                              fontWeight: isDiffSite ? 700 : 500,
                            }}>
                              {isDiffSite && <span title={`Site: ${schSite?.name}`}>⇄</span>}
                              {s.start_time?.slice(0,5)}–{s.end_time?.slice(0,5)}
                              {can('manager') && (
                                <button onClick={e=>delSchedule(s.id,e)}
                                  style={{background:'none',border:'none',cursor:'pointer',
                                    padding:0,lineHeight:1,marginLeft:2,color:'inherit',opacity:0.5}}>✕</button>
                              )}
                            </div>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {allDisplayed.length===0 && (
              <tr><td colSpan={8} style={{textAlign:'center',padding:30,color:'var(--text-3)'}}>
                Aucun collaborateur{filterSite?' sur ce site':''}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Indicateur charge semaine */}
      {viewMode==='week' && allDisplayed.length > 0 && (
        <div style={{marginTop:12,display:'flex',gap:8,flexWrap:'wrap'}}>
          {allDisplayed.filter(u=>u.role!=='freelance').map(u=>{
            const weekHours = schedules
              .filter(s=>s.user_id===u.id)
              .reduce((sum,s)=>{
                if (!s.start_time||!s.end_time) return sum;
                const [sh,sm]=s.start_time.slice(0,5).split(':').map(Number);
                const [eh,em]=s.end_time.slice(0,5).split(':').map(Number);
                let mins=(eh*60+em)-(sh*60+sm);
                if (mins<0) mins+=24*60;
                return sum+mins;
              },0);
            if (weekHours===0) return null;
            const h=Math.floor(weekHours/60), m=weekHours%60;
            return (
              <div key={u.id} style={{fontSize:11,padding:'3px 10px',borderRadius:20,
                background:'rgba(124,92,252,0.1)',color:'var(--accent-light)',border:'1px solid rgba(124,92,252,0.2)'}}>
                {u.first_name} : {h}h{m>0?String(m).padStart(2,'0'):''}
              </div>
            );
          }).filter(Boolean)}
        </div>
      )}

      {/* Vue mois */}
      {viewMode==='month' && (() => {
        const monthStart = new Date(format(weekStart,'yyyy-MM')+'-01');
        const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth()+1, 0);
        const days = [];
        for (let d=new Date(monthStart); d<=monthEnd; d.setDate(d.getDate()+1)) {
          days.push(format(new Date(d),'yyyy-MM-dd'));
        }
        // Grouper par semaine
        const weeks = [];
        let week = [];
        for (const day of days) {
          const dow = new Date(day).getDay();
          const adjustedDow = dow===0?6:dow-1;
          if (adjustedDow===0 && week.length>0) { weeks.push(week); week=[]; }
          week.push(day);
        }
        if (week.length>0) weeks.push(week);

        return (
          <div className="card" style={{padding:0,overflowX:'auto',marginTop:0}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{borderBottom:'1px solid var(--border)'}}>
                  {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d=>(
                    <th key={d} style={{padding:'8px 4px',textAlign:'center',fontSize:12,color:'var(--text-3)',fontWeight:600}}>{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.map((week,wi)=>(
                  <tr key={wi} style={{borderBottom:'1px solid var(--border)'}}>
                    {Array.from({length:7},(_,i)=>{
                      const dow = i; // 0=Lun
                      const day = week.find(d=>{const js=new Date(d).getDay(); return (js===0?6:js-1)===dow;});
                      if (!day) return <td key={i} style={{padding:'6px 4px',background:'var(--bg-base)',minHeight:80}}/>;
                      const isToday = day===format(new Date(),'yyyy-MM-dd');
                      const daySchedules = schedules.filter(s=>(s.work_date_str||s.work_date?.slice(0,10))===day);
                      return (
                        <td key={i} style={{padding:'4px',verticalAlign:'top',minWidth:80,
                          background:isToday?'rgba(124,92,252,0.04)':'transparent',
                          border:'1px solid var(--border)'}}>
                          <div style={{fontSize:11,fontWeight:isToday?700:400,
                            color:isToday?'var(--accent-light)':'var(--text-3)',marginBottom:3}}>
                            {format(new Date(day),'d')}
                          </div>
                          {daySchedules.map(s=>{
                            const u = allDisplayed.find(u=>u.id===s.user_id);
                            if (!u) return null;
                            return (
                              <div key={s.id} style={{fontSize:10,padding:'2px 5px',borderRadius:4,marginBottom:2,
                                background:'rgba(124,92,252,0.12)',color:'var(--accent-light)',
                                whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                                {u.first_name} {s.start_time?.slice(0,5)}
                              </div>
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Modal ajout horaire */}
      {showAdd && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAdd(false)}>
          <div className="modal">
            <h3 className="modal-title">Ajouter des horaires</h3>
            <form onSubmit={addSchedule}>
              <div className="form-group">
                <label className="form-label">Collaborateur</label>
                <select className="input" value={newEntry.userId}
                  onChange={e=>setNewEntry(n=>({...n,userId:e.target.value}))} required>
                  <option value="">Choisir…</option>
                  {staffUsers.map(u=>(
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.role})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input type="date" className="input" value={newEntry.workDate}
                  onChange={e=>setNewEntry(n=>({...n,workDate:e.target.value}))} required/>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Début</label>
                  <input type="time" className="input" value={newEntry.startTime}
                    onChange={e=>setNewEntry(n=>({...n,startTime:e.target.value}))} required/>
                </div>
                <div className="form-group">
                  <label className="form-label">Fin</label>
                  <input type="time" className="input" value={newEntry.endTime}
                    onChange={e=>setNewEntry(n=>({...n,endTime:e.target.value}))} required/>
                </div>
              </div>
              {sites.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Site d'affectation</label>
                  <select className="input" value={newEntry.siteId}
                    onChange={e=>setNewEntry(n=>({...n,siteId:e.target.value}))}>
                    <option value="">Site par défaut</option>
                    {sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAdd(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal appliquer template */}
      {showApply && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowApply(false)}>
          <div className="modal">
            <h3 className="modal-title">Appliquer un template</h3>
            <p className="text-sm text-muted" style={{marginBottom:12}}>
              À partir du {format(weekStart,'d MMMM yyyy',{locale:fr})}
            </p>
            <div className="form-group" style={{marginBottom:16}}>
              <label className="form-label">Nombre de semaines</label>
              <div style={{display:'flex', alignItems:'center', gap:10}}>
                <input type="number" className="input" style={{width:80}} min={1} max={52}
                  value={applyWeeks} onChange={e=>setApplyWeeks(Math.max(1,parseInt(e.target.value)||1))}/>
                <span style={{fontSize:13,color:'var(--text-3)'}}>
                  semaine(s) — jusqu'au {format(new Date(weekStart.getTime() + (applyWeeks-1)*7*86400000 + 6*86400000),'d MMM yyyy',{locale:fr})}
                </span>
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:20}}>
              {templates.map(t=>(
                <div key={t.id} style={{background:'var(--bg-elevated)',borderRadius:10,padding:14}}>
                  <div style={{fontWeight:600,marginBottom:4}}>{t.name}</div>
                  {t.description && <div className="text-sm text-muted" style={{marginBottom:10}}>{t.description}</div>}
                  <div style={{fontSize:12,color:'var(--text-3)',marginBottom:10}}>{t.line_count} ligne(s)</div>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-primary btn-sm" onClick={()=>applyTemplate(t.id,false)}>
                      Appliquer (sans écraser)
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={()=>applyTemplate(t.id,true)}>
                      Appliquer (écraser)
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowApply(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Absences ──────────────────────────────────────────────────
function AbsencesTab({ can, user, sites }) {
  const [absences, setAbsences]   = useState([]);
  const [month, setMonth]         = useState(format(new Date(),'yyyy-MM'));
  const [filterSite, setFilterSite] = useState('');
  const [filterStatus, setStatus] = useState('');
  const [showNew, setShowNew]     = useState(false);
  const [users, setUsers]         = useState([]);
  const [form, setForm] = useState({userId:'',type:'conge_paye',dateFrom:'',dateTo:'',reason:''});
  const [soldes, setSoldes] = useState({conge_paye:0,rtt:0});

  const load = useCallback(async () => {
    const params = {month};
    if (filterSite) params.siteId = filterSite;
    if (filterStatus) params.status = filterStatus;
    try {
      const r = await absencesApi.list(params);
      setAbsences(r.data.absences);
    } catch { toast.error('Erreur chargement absences'); }
  }, [month, filterSite, filterStatus]);

  useEffect(()=>{load();},[load]);
  useEffect(()=>{
    if (can('manager')) authApi.users().then(r=>setUsers(r.data.users)).catch(()=>{});
    // Calculer soldes sur l'année en cours
    const year = new Date().getFullYear();
    absencesApi.list({month:`${year}-01`}).then(r=>{
      const approved = (r.data.absences||[]).filter(a=>a.status==='approved');
      const cp = approved.filter(a=>a.type==='conge_paye')
        .reduce((s,a)=>{
          const d1=new Date(String(a.date_from).slice(0,10)+'T12:00:00');
          const d2=new Date(String(a.date_to).slice(0,10)+'T12:00:00');
          return s+Math.round((d2-d1)/86400000)+1;
        },0);
      const rtt = approved.filter(a=>a.type==='rtt')
        .reduce((s,a)=>{
          const d1=new Date(String(a.date_from).slice(0,10)+'T12:00:00');
          const d2=new Date(String(a.date_to).slice(0,10)+'T12:00:00');
          return s+Math.round((d2-d1)/86400000)+1;
        },0);
      setSoldes({conge_paye:cp, rtt});
    }).catch(()=>{});
  },[can]);

  const submit = async e => {
    e.preventDefault();
    try {
      await absencesApi.create({...form, userId: form.userId||user.id});
      toast.success('Absence créée');
      setShowNew(false); load();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  const validate = async (id, status) => {
    try {
      await absencesApi.validate(id, {status});
      toast.success(status==='approved'?'Absence validée':'Absence refusée');
      load();
    } catch { toast.error('Erreur'); }
  };

  const remove = async (id) => {
    try { await absencesApi.remove(id); toast.success('Absence supprimée'); load(); }
    catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  const f = k => ({
    value: form[k],
    onChange: e => {
      const val = e.target.value;
      setForm(p => {
        const next = {...p, [k]: val};
        // Quand dateFrom change, dateTo = dateFrom si dateTo vide ou antérieur
        if (k === 'dateFrom' && (!p.dateTo || p.dateTo < val)) {
          next.dateTo = val;
        }
        return next;
      });
    }
  });

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(prevMonth(month))}><ChevronLeft size={15}/></button>
        <span style={{fontWeight:600,textTransform:'capitalize'}}>
          {format(new Date(month+'-15'),'MMMM yyyy',{locale:fr})}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(nextMonth(month))}><ChevronRight size={15}/></button>

        {can('manager') && sites.length > 0 && (
          <select className="input btn-sm" style={{width:'auto'}} value={filterSite} onChange={e=>setFilterSite(e.target.value)}>
            <option value="">Tous les sites</option>
            {sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <select className="input btn-sm" style={{width:'auto'}} value={filterStatus} onChange={e=>setStatus(e.target.value)}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_LABEL).map(([k,v])=><option key={k} value={k}>{v}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" style={{marginLeft:'auto'}} onClick={()=>setShowNew(true)}>
          <Plus size={14}/> Nouvelle absence
        </button>
      </div>

      {/* Soldes absences */}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        {[
          ['Congés payés pris',soldes.conge_paye,'var(--teal)'],
          ['RTT pris',soldes.rtt,'var(--accent-light)'],
        ].map(([label,val,color])=>(
          <div key={label} style={{padding:'10px 16px',borderRadius:10,background:'var(--bg-elevated)',
            border:'1px solid var(--border)',display:'flex',gap:10,alignItems:'center'}}>
            <span style={{fontSize:12,color:'var(--text-3)'}}>{label}</span>
            <span style={{fontSize:18,fontWeight:800,color}}>{val}j</span>
          </div>
        ))}
      </div>

      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {can('manager') && <th>Collaborateur</th>}
                <th>Type</th><th>Du</th><th>Au</th><th>Durée</th>
                <th>Motif</th><th>Statut</th>
                {can('manager') && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {absences.length===0 ? (
                <tr><td colSpan={8} style={{textAlign:'center',padding:30,color:'var(--text-3)'}}>
                  Aucune absence ce mois
                </td></tr>
              ) : absences.map(a=>{
                const dfrom = new Date(String(a.date_from).slice(0,10)+'T12:00:00');
                const dto = new Date(String(a.date_to).slice(0,10)+'T12:00:00');
                const days = (!isNaN(dfrom) && !isNaN(dto)) ? Math.round((dto-dfrom)/86400000)+1 : '?';
                const siteColor = sites.find(s=>s.id===a.site_id)?.color;
                return (
                  <tr key={a.id}>
                    {can('manager') && (
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          {siteColor && <div style={{width:3,height:20,borderRadius:2,background:siteColor}}/>}
                          <div>
                            <div style={{fontWeight:600,fontSize:13}}>{a.first_name} {a.last_name}</div>
                            {a.site_name && <div style={{fontSize:11,color:'var(--text-3)'}}>{a.site_name}</div>}
                          </div>
                        </div>
                      </td>
                    )}
                    <td>
                      <span style={{fontWeight:600,color:ABSENCE_COLORS[a.type]||'var(--text-2)',fontSize:13}}>
                        {ABSENCE_LABELS[a.type]||a.type}
                      </span>
                    </td>
                    <td>{safeFormat(String(a.date_from).slice(0,10)+'T12:00:00','d MMM yyyy',{locale:fr})}</td>
                    <td>{safeFormat(String(a.date_to).slice(0,10)+'T12:00:00','d MMM yyyy',{locale:fr})}</td>
                    <td>{days} jour{days>1?'s':''}</td>
                    <td className="text-sm text-muted">{a.reason||'—'}</td>
                    <td><span className={`badge ${STATUS_BADGE[a.status]}`}>{STATUS_LABEL[a.status]}</span></td>
                    {can('manager') && (
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          {a.status==='pending' && (
                            <>
                              <button className="btn btn-teal btn-sm" style={{padding:'4px 8px'}}
                                onClick={()=>validate(a.id,'approved')}>
                                <CheckCircle2 size={13}/>
                              </button>
                              <button className="btn btn-danger btn-sm" style={{padding:'4px 8px'}}
                                onClick={()=>validate(a.id,'rejected')}>
                                <XCircle size={13}/>
                              </button>
                            </>
                          )}
                          <button className="btn btn-ghost btn-sm" style={{padding:'4px 8px'}}
                            onClick={()=>remove(a.id)}>
                            <Trash2 size={13} color="var(--red)"/>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowNew(false)}>
          <div className="modal">
            <h3 className="modal-title">Nouvelle absence</h3>
            <form onSubmit={submit}>
              {can('manager') && (
                <div className="form-group">
                  <label className="form-label">Collaborateur</label>
                  <select className="input" {...f('userId')}>
                    <option value="">Moi-même</option>
                    {users.map(u=><option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select className="input" required {...f('type')}>
                  {Object.entries(ABSENCE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Du *</label>
                  <input type="date" className="input" required {...f('dateFrom')}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Au *</label>
                  <input type="date" className="input" required {...f('dateTo')}/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Motif</label>
                <textarea className="input" rows={2} {...f('reason')} style={{resize:'vertical'}}/>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowNew(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Templates ─────────────────────────────────────────────────
function TemplatesTab({ can }) {
  const [templates, setTemplates] = useState([]);
  const [users, setUsers]         = useState([]);
  const [sites, setSites]         = useState([]);
  const [showNew, setShowNew]     = useState(false);
  const [editing, setEditing]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]   = useState(false);

  const load = () => templatesApi.list().then(r=>setTemplates(r.data.templates));
  useEffect(()=>{
    load();
    if (can('manager')) authApi.users().then(r=>setUsers(r.data.users.filter(u=>u.role!=='freelance'))).catch(()=>{});
    sitesApi.list().then(r=>setSites(r.data.sites)).catch(()=>{});
  },[]);

  const del = async () => {
    setDeleting(true);
    try {
      await templatesApi.remove(confirmDelete.id);
      toast.success('Supprimé');
      setConfirmDelete(null);
      load();
    } catch { toast.error('Erreur'); }
    finally { setDeleting(false); }
  };

  const duplicate = async (t) => {
    try {
      const r = await templatesApi.get(t.id);
      const lines = r.data.lines.map(l=>({
        userId:l.user_id, dayOfWeek:l.day_of_week,
        startTime:l.start_time?.slice(0,5), endTime:l.end_time?.slice(0,5),
        siteId:l.site_id||'', breakMin:l.break_min||0
      }));
      await templatesApi.create({name:`${t.name} (copie)`, description:t.description||'', lines});
      toast.success('Template dupliqué'); load();
    } catch { toast.error('Erreur'); }
  };

  if (!can('manager')) return <div className="empty-state"><p>Accès réservé aux managers</p></div>;

  return (
    <div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:20}}>
        <button className="btn btn-primary btn-sm" onClick={()=>setShowNew(true)}>
          <Plus size={14}/> Nouveau template
        </button>
      </div>

      {templates.length===0 ? (
        <div className="empty-state card">
          <p>Aucun template — créez-en un pour planifier rapidement des semaines récurrentes</p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {templates.map(t=>(
            <div key={t.id} className="card">
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:15}}>{t.name}</div>
                  {t.description && <div className="text-sm text-muted" style={{marginTop:2}}>{t.description}</div>}
                  <div style={{fontSize:12,color:'var(--text-3)',marginTop:4}}>
                    {t.line_count} ligne(s) · Créé par {t.creator_first} {t.creator_last}
                  </div>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-secondary btn-sm" onClick={()=>setEditing(t)}>
                    Modifier
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={()=>duplicate(t)}
                    title="Dupliquer ce template">
                    ⧉
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={()=>setConfirmDelete(t)}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showNew||editing) && (
        <TemplateEditor
          template={editing}
          users={users}
          sites={sites}
          onClose={()=>{setShowNew(false);setEditing(null);}}
          onSaved={()=>{load();setShowNew(false);setEditing(null);}}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Supprimer le template"
          message={`Supprimer "${confirmDelete.name}" ?`}
          confirmLabel="Supprimer"
          danger
          loading={deleting}
          onConfirm={del}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// Jours dans l'ordre lun-dim pour l'UI
const WEEK_DAYS_ORDERED = [
  {idx:1,label:'Lun'},{idx:2,label:'Mar'},{idx:3,label:'Mer'},
  {idx:4,label:'Jeu'},{idx:5,label:'Ven'},{idx:6,label:'Sam'},{idx:0,label:'Dim'},
];

// Un bloc "collaborateur" = un user + N jours avec horaires
// On convertit vers/depuis le format de lignes individuelles pour l'API
function linesFromBlocks(blocks) {
  const lines = [];
  for (const b of blocks) {
    if (!b.userId) continue;
    for (const day of b.days) {
      if (!day.active) continue;
      lines.push({
        userId: b.userId,
        dayOfWeek: day.idx,
        startTime: day.start || '09:00',
        endTime: day.end || '18:00',
        siteId: b.siteId || '',
        breakMin: 0,
      });
    }
  }
  return lines;
}

function blocksFromLines(lines, users) {
  const byUser = {};
  for (const l of lines) {
    if (!byUser[l.user_id]) {
      byUser[l.user_id] = {
        userId: l.user_id,
        siteId: l.site_id || '',
        defaultStart: l.start_time?.slice(0,5) || '09:00',
        defaultEnd: l.end_time?.slice(0,5) || '18:00',
        days: WEEK_DAYS_ORDERED.map(d => ({ ...d, active: false, start: '', end: '' })),
      };
    }
    const block = byUser[l.user_id];
    const day = block.days.find(d => d.idx === l.day_of_week);
    if (day) {
      day.active = true;
      day.start = l.start_time?.slice(0,5) || '';
      day.end = l.end_time?.slice(0,5) || '';
    }
  }
  return Object.values(byUser);
}

function TemplateEditor({ template, users, sites, onClose, onSaved }) {
  const [name, setName]       = useState(template?.name||'');
  const [desc, setDesc]       = useState(template?.description||'');
  const [blocks, setBlocks]   = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(()=>{
    if (template) {
      templatesApi.get(template.id).then(r=>{
        setBlocks(blocksFromLines(r.data.lines, users));
      });
    }
  },[template?.id]);

  const addBlock = () => setBlocks(prev => [...prev, {
    userId: '',
    siteId: sites[0]?.id || '',
    days: WEEK_DAYS_ORDERED.map(d => ({ ...d, active: false, start: '', end: '' })),
  }]);

  const removeBlock = idx => setBlocks(prev => prev.filter((_,i) => i !== idx));

  const updBlock = (idx, key, val) => setBlocks(prev =>
    prev.map((b, i) => i === idx ? { ...b, [key]: val } : b)
  );

  const toggleDay = (blockIdx, dayIdx) => setBlocks(prev =>
    prev.map((b, i) => i !== blockIdx ? b : {
      ...b,
      days: b.days.map(d => d.idx === dayIdx ? { ...d, active: !d.active } : d),
    })
  );

  const updDayTime = (blockIdx, dayIdx, key, val) => setBlocks(prev =>
    prev.map((b, i) => i !== blockIdx ? b : {
      ...b,
      days: b.days.map(d => d.idx === dayIdx ? { ...d, [key]: val } : d),
    })
  );

  const submit = async e => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Nom requis');
    const lines = linesFromBlocks(blocks);
    if (lines.length === 0) return toast.error('Ajoutez au moins une ligne');
    setLoading(true);
    try {
      if (template) await templatesApi.update(template.id, {name,description:desc,lines});
      else await templatesApi.create({name,description:desc,lines});
      toast.success(template?'Template mis à jour':'Template créé');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:720}}>
        <h3 className="modal-title">{template?'Modifier le template':'Nouveau template'}</h3>
        <form onSubmit={submit}>
          <div className="form-row" style={{marginBottom:20}}>
            <div className="form-group">
              <label className="form-label">Nom *</label>
              <input className="input" required value={name} onChange={e=>setName(e.target.value)}
                placeholder="Ex: Semaine type, Planning été…"/>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="input" value={desc} onChange={e=>setDesc(e.target.value)}/>
            </div>
          </div>

          <div style={{fontWeight:600,fontSize:14,marginBottom:12}}>Collaborateurs & horaires</div>

          {blocks.length === 0 && (
            <div className="empty-state" style={{padding:'20px 0',marginBottom:12}}>
              <p>Ajoutez un collaborateur pour commencer</p>
            </div>
          )}

          {blocks.map((block, blockIdx) => (
            <div key={blockIdx} style={{
              background:'var(--bg-elevated)', borderRadius:12, padding:16,
              marginBottom:12, border:`1px solid ${block.userId ? 'rgba(124,92,252,0.3)' : 'var(--border)'}`,
            }}>
              {/* Sélection collaborateur */}
              <div style={{marginBottom:12}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                  <span style={{fontSize:12, fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em'}}>Collaborateur</span>
                  <button type="button" className="btn btn-ghost btn-sm" style={{padding:4}}
                    onClick={() => removeBlock(blockIdx)}>
                    <Trash2 size={13} color="var(--red)"/>
                  </button>
                </div>
                <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                  {users.map(u => {
                    const sel = block.userId === u.id;
                    return (
                      <button key={u.id} type="button" onClick={() => updBlock(blockIdx,'userId',u.id)}
                        style={{
                          display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                          borderRadius:8, border:`1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                          background: sel ? 'rgba(124,92,252,0.15)' : 'var(--bg-base)',
                          color: sel ? 'var(--accent-light)' : 'var(--text-2)',
                          cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight: sel ? 600 : 400,
                          transition:'all 0.12s',
                        }}>
                        <div style={{width:22, height:22, borderRadius:'50%', background:'linear-gradient(135deg,var(--accent),var(--teal))',
                          display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#fff', flexShrink:0}}>
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </div>
                        {u.first_name} {u.last_name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Site */}
              {sites.length > 0 && (
                <div style={{marginBottom:12}}>
                  <span style={{fontSize:12, fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:6}}>Site</span>
                  <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                    {[{id:'',name:'Défaut', color:'var(--text-3)'}, ...sites].map(s => {
                      const sel = (block.siteId||'') === s.id;
                      return (
                        <button key={s.id} type="button" onClick={() => updBlock(blockIdx,'siteId',s.id)}
                          style={{
                            padding:'5px 14px', borderRadius:8, border:`1px solid ${sel ? (s.color||'var(--accent)') : 'var(--border)'}`,
                            background: sel ? `${s.color||'var(--accent)'}22` : 'var(--bg-base)',
                            color: sel ? (s.color||'var(--accent-light)') : 'var(--text-2)',
                            cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight: sel ? 600 : 400,
                          }}>
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Jours + horaires */}
              <div>
                <span style={{fontSize:12, fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:8}}>Jours & horaires</span>
                <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                  {block.days.map(day => (
                    <div key={day.idx} style={{display:'flex', flexDirection:'column', alignItems:'center', gap:4}}>
                      <button type="button" onClick={() => toggleDay(blockIdx, day.idx)}
                        style={{
                          width:44, padding:'7px 0', borderRadius:8, border:'none', cursor:'pointer',
                          fontFamily:'inherit', fontSize:12, fontWeight:700,
                          background: day.active ? 'var(--accent)' : 'var(--bg-base)',
                          color: day.active ? '#fff' : 'var(--text-3)',
                          transition:'all 0.12s',
                        }}>
                        {day.label}
                      </button>
                      {day.active && (
                        <div style={{display:'flex', flexDirection:'column', gap:2}}>
                          <input type="time" value={day.start || '09:00'}
                            onChange={e => updDayTime(blockIdx, day.idx, 'start', e.target.value)}
                            style={{width:80, fontSize:12, padding:'5px 6px',
                              background:'rgba(0,210,180,0.08)',
                              border:'1px solid rgba(0,210,180,0.3)', borderRadius:7,
                              color:'var(--teal)', fontFamily:'monospace', textAlign:'center',
                              outline:'none', fontWeight:600}}/>
                          {(() => {
                          const endVal = day.end || '18:00';
                          const startVal = day.start || '09:00';
                          const isNextDay = endVal < startVal;
                          return (
                            <div style={{position:'relative'}}>
                              <input type="time" value={endVal}
                                onChange={e => updDayTime(blockIdx, day.idx, 'end', e.target.value)}
                                style={{width:80, fontSize:12, padding:'5px 6px',
                                  background: isNextDay ? 'rgba(124,92,252,0.08)' : 'rgba(245,166,35,0.08)',
                                  border:`1px solid ${isNextDay ? 'rgba(124,92,252,0.4)' : 'rgba(245,166,35,0.3)'}`,
                                  borderRadius:7,
                                  color: isNextDay ? 'var(--accent-light)' : 'var(--amber)',
                                  fontFamily:'monospace', textAlign:'center',
                                  outline:'none', fontWeight:600}}/>
                              {isNextDay && (
                                <div style={{fontSize:9, color:'var(--accent-light)', textAlign:'center', marginTop:1}}>
                                  +1j
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

          <button type="button" className="btn btn-secondary btn-sm" onClick={addBlock} style={{marginBottom:16}}>
            <Plus size={13}/> Ajouter un collaborateur
          </button>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" style={{width:16,height:16}}/> : (template?'Mettre à jour':'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Mes heures ────────────────────────────────────────────────
function MyHours({ currentUserId }) {
  const [entries, setEntries]       = useState([]);
  const [scheduled, setScheduled]   = useState([]);
  const [month, setMonth]           = useState(format(new Date(),'yyyy-MM'));
  const [viewScope, setViewScope]   = useState('month'); // 'month' | 'year'
  const [loading, setLoading]       = useState(false);

  const year = month.slice(0,4);

  useEffect(()=>{
    setLoading(true);
    if (viewScope === 'month') {
      Promise.all([
        scheduleApi.myTime({month}),
        scheduleApi.list({month}),
      ]).then(([r,s])=>{
      setEntries(r.data.entries);
      // Filtrer uniquement mes horaires programmés
      const allSch = s.data.schedules||[];
      setScheduled(currentUserId ? allSch.filter(s=>s.user_id===currentUserId) : allSch);
    })
      .catch(()=>toast.error('Erreur chargement'))
      .finally(()=>setLoading(false));
    } else {
      // Année : charger tous les mois
      const months = Array.from({length:12},(_,i)=>`${year}-${String(i+1).padStart(2,'0')}`);
      Promise.all(months.map(m=>scheduleApi.myTime({month:m}).catch(()=>({data:{entries:[]}}))))
        .then(results=>{ setEntries(results.flatMap(r=>r.data.entries||[])); })
        .catch(()=>toast.error('Erreur chargement'))
        .finally(()=>setLoading(false));
    }
  },[month, viewScope, year]);

  const totalMin = entries.filter(e=>e.clock_out).reduce((s,e)=>s+(parseInt(e.duration_min)||0),0);
  const jours    = new Set(entries.filter(e=>e.clock_out).map(e=>(e.work_date_str||e.work_date)?.slice(0,10))).size;

  // Heures programmées depuis le planning
  const scheduledMin = scheduled.reduce((sum,s)=>{
    if (!s.start_time||!s.end_time) return sum;
    const [sh,sm]=s.start_time.slice(0,5).split(':').map(Number);
    const [eh,em]=s.end_time.slice(0,5).split(':').map(Number);
    let mins=(eh*60+em)-(sh*60+sm);
    if (mins<0) mins+=24*60; // nuit
    return sum+mins;
  },0);
  const diffMin  = totalMin - scheduledMin;

  // Données par jour pour mini graphique
  const byDay = {};
  for (const e of entries.filter(e=>e.clock_out)) {
    const d = (e.work_date_str||e.work_date)?.slice(0,10)||'';
    if (!byDay[d]) byDay[d]=0;
    byDay[d] += parseInt(e.duration_min)||0;
  }
  const maxMin = Math.max(...Object.values(byDay), 1);

  const exportCSV = () => {
    const header = 'Date,Entrée,Sortie,Durée (min)';
    const rows = entries.map(e=>[
      (e.work_date_str||e.work_date)?.slice(0,10),
      e.clock_in ? new Date(e.clock_in).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}) : '',
      e.clock_out ? new Date(e.clock_out).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}) : '',
      e.duration_min||''
    ].join(','));
    const csv = [header,...rows].join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href=url; a.download=`heures-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:20,flexWrap:'wrap'}}>
        {/* Toggle mois/année */}
        <div style={{display:'flex',gap:0,borderRadius:8,overflow:'hidden',border:'1px solid var(--border)'}}>
          {[['month','Mois'],['year','Année']].map(([v,l])=>(
            <button key={v} onClick={()=>setViewScope(v)}
              style={{padding:'5px 14px',border:'none',cursor:'pointer',fontFamily:'inherit',fontSize:12,fontWeight:600,
                background:viewScope===v?'var(--accent)':'var(--bg-elevated)',
                color:viewScope===v?'#fff':'var(--text-3)'}}>
              {l}
            </button>
          ))}
        </div>

        {viewScope==='month' ? (
          <>
            <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(prevMonth(month))}><ChevronLeft size={15}/></button>
            <span style={{fontWeight:600,textTransform:'capitalize'}}>{format(new Date(month+'-15'),'MMMM yyyy',{locale:fr})}</span>
            <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(nextMonth(month))}><ChevronRight size={15}/></button>
          </>
        ) : (
          <>
            <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(m=>`${parseInt(m.slice(0,4))-1}-${m.slice(5)}`)}>
              <ChevronLeft size={15}/>
            </button>
            <span style={{fontWeight:600}}>{year}</span>
            <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(m=>`${parseInt(m.slice(0,4))+1}-${m.slice(5)}`)}>
              <ChevronRight size={15}/>
            </button>
          </>
        )}

        <button className="btn btn-secondary btn-sm" style={{marginLeft:'auto'}} onClick={exportCSV}>
          ↓ Export CSV
        </button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:20}}>
        {[
          ['Heures effectuées',`${Math.floor(totalMin/60)}h${String(totalMin%60).padStart(2,'0')}`,'var(--teal)'],
          ['Heures programmées',scheduledMin>0?`${Math.floor(scheduledMin/60)}h${String(scheduledMin%60).padStart(2,'0')}`:'—','var(--text-2)'],
          ['Écart',scheduledMin>0?(diffMin>=0?`+${Math.floor(diffMin/60)}h${String(Math.abs(diffMin)%60).padStart(2,'0')}`:`-${Math.floor(Math.abs(diffMin)/60)}h${String(Math.abs(diffMin)%60).padStart(2,'0')}`):'—',diffMin>=0?'var(--teal)':'var(--red)'],
          ['Jours travaillés',jours,'var(--accent-light)'],
        ].map(([label,val,color])=>(
          <div key={label} className="card">
            <div className="text-xs text-muted" style={{marginBottom:4}}>{label}</div>
            <div style={{fontSize:24,fontWeight:700,color}}>{val}</div>
          </div>
        ))}
      </div>

      {/* Mini graphique barres */}
      {Object.keys(byDay).length > 0 && (
        <div className="card" style={{marginBottom:16}}>
          <div style={{fontWeight:600,fontSize:13,marginBottom:12,color:'var(--text-2)'}}>Heures par jour</div>
          <div style={{display:'flex',gap:3,alignItems:'flex-end',height:60}}>
            {Object.entries(byDay).sort(([a],[b])=>a.localeCompare(b)).map(([day,mins])=>{
              const pct = mins/maxMin*100;
              const h=Math.floor(mins/60),m=mins%60;
              return (
                <div key={day} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}
                  title={`${format(new Date(day+'T12:00:00'),'EEE d',{locale:fr})} — ${h}h${m>0?String(m).padStart(2,'0'):''}`}>
                  <div style={{width:'100%',background:'var(--accent)',borderRadius:'3px 3px 0 0',
                    height:`${pct}%`,minHeight:2,transition:'height 0.3s'}}/>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading?<div className="empty-state"><span className="spinner"/></div>:(
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Entrée</th><th>Sortie</th><th>Durée</th><th>Statut</th></tr></thead>
              <tbody>
                {entries.length===0?(
                  <tr><td colSpan={5} style={{textAlign:'center',padding:30,color:'var(--text-3)'}}>Aucun pointage ce mois</td></tr>
                ):entries.map(e=>{
                  const dur=parseInt(e.duration_min)||0;
                  return (
                    <tr key={e.id}>
                      <td className="bold">{safeFormat(typeof e.work_date==='string'?e.work_date.slice(0,10)+'T12:00:00':e.work_date,'EEE d MMM yyyy',{locale:fr})}</td>
                      <td className="font-mono">{e.clock_in?safeFormat(e.clock_in,'HH:mm'):'—'}</td>
                      <td className="font-mono">{e.clock_out?safeFormat(e.clock_out,'HH:mm'):<span className="badge badge-amber" style={{fontSize:10}}>En cours</span>}</td>
                      <td style={{fontWeight:600,color:dur>0?'var(--teal)':'var(--text-3)'}}>
                        {dur>0?`${Math.floor(dur/60)}h${String(dur%60).padStart(2,'0')}`:'—'}
                      </td>
                      <td><span className={`badge ${e.clock_out?'badge-green':'badge-amber'}`}>{e.clock_out?'Terminé':'En cours'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Gestion admin des pointages ───────────────────────────────
function AllTimeEntries({ can }) {
  const [entries, setEntries]   = useState([]);
  const [openEntries, setOpen]  = useState([]);
  const [month, setMonth]       = useState(format(new Date(),'yyyy-MM'));
  const [loading, setLoading]   = useState(false);
  const [editModal, setEdit]    = useState(null);
  const [confirmDeleteEntry, setConfirmDeleteEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(false);

  const load = useCallback(() => {
    if (!can('manager')) return;
    setLoading(true);
    api.get('/schedule/time-entries',{params:{month}})
      .then(r => setEntries(r.data.entries))
      .catch(() => toast.error('Erreur chargement pointages'))
      .finally(() => setLoading(false));
    scheduleApi.openEntries()
      .then(r => setOpen(r.data.entries))
      .catch(() => {});
  }, [month, can]);

  useEffect(() => { load(); }, [load]);

  if (!can('manager')) return <div className="empty-state"><p>Accès réservé aux managers</p></div>;

  const [pinSetModal, setPinSet] = useState(null);
  const [newPin, setNewPin]       = useState('');

  const exportCSV = () => {
    const header = 'Collaborateur,Date,Entrée,Sortie,Durée (min)';
    const rows = entries.map(e=>[
      `${e.first_name} ${e.last_name}`,
      (e.work_date_str||e.work_date)?.slice(0,10),
      e.clock_in?new Date(e.clock_in).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}):'',
      e.clock_out?new Date(e.clock_out).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}):'',
      e.duration_min||''
    ].join(','));
    const csv=[header,...rows].join('\n');
    const blob=new Blob([csv],{type:'text/csv'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url;a.download=`pointages-${month}.csv`;a.click();
    URL.revokeObjectURL(url);
  };

  const closeEntry = async (id) => {
    try { await scheduleApi.closeEntry(id); toast.success('Pointage clôturé'); load(); }
    catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  const deleteEntry = async () => {
    setDeletingEntry(true);
    try {
      await scheduleApi.deleteEntry(confirmDeleteEntry);
      toast.success('Pointage supprimé');
      setConfirmDeleteEntry(null);
      load();
    } catch { toast.error('Erreur'); }
    finally { setDeletingEntry(false); }
  };

  const byUser = {};
  for (const e of entries) {
    const key = `${e.first_name} ${e.last_name}`;
    if (!byUser[key]) byUser[key] = { entries:[], totalMin:0 };
    byUser[key].entries.push(e);
    byUser[key].totalMin += parseInt(e.duration_min)||0;
  }

  return (
    <div>
      {/* Pointages ouverts */}
      {openEntries.length > 0 && (
        <div className="card" style={{marginBottom:20,borderColor:'var(--amber)'}}>
          <div style={{fontWeight:600,fontSize:14,marginBottom:12,color:'var(--amber)',display:'flex',alignItems:'center',gap:6}}>
            <Clock size={14}/> {openEntries.length} pointage(s) encore ouvert(s)
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Collaborateur</th><th>Entrée</th><th>Durée en cours</th><th>Actions</th></tr></thead>
              <tbody>
                {openEntries.map(e => {
                  const dur = Math.floor((Date.now() - new Date(e.clock_in).getTime()) / 60000);
                  return (
                    <tr key={e.id}>
                      <td className="bold">{e.first_name} {e.last_name}</td>
                      <td className="font-mono">{safeFormat(e.clock_in,'HH:mm dd/MM')}</td>
                      <td style={{color:'var(--amber)',fontWeight:600}}>
                        {Math.floor(dur/60)}h{String(dur%60).padStart(2,'0')} en cours
                      </td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          <button className="btn btn-teal btn-sm" onClick={() => closeEntry(e.id)}>
                            Clôturer maintenant
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEdit(e)}>
                            Modifier
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeleteEntry(e.id)}>
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alertes anomalies */}
      {(() => {
        const anomalies = entries.filter(e => {
          if (!e.clock_out) return false;
          const dur = parseInt(e.duration_min)||0;
          return dur > 12*60 || dur < 15; // > 12h ou < 15 min
        });
        if (!anomalies.length) return null;
        return (
          <div className="card" style={{marginBottom:16,borderColor:'var(--amber)'}}>
            <div style={{fontWeight:600,fontSize:13,color:'var(--amber)',marginBottom:8}}>
              ⚠ {anomalies.length} anomalie(s) détectée(s)
            </div>
            {anomalies.map(e=>{
              const dur=parseInt(e.duration_min)||0;
              return (
                <div key={e.id} style={{fontSize:12,color:'var(--text-2)',padding:'4px 0',
                  borderBottom:'1px solid var(--border)'}}>
                  <strong>{e.first_name} {e.last_name}</strong> — {safeFormat(e.work_date+'T12:00:00','d MMM',{locale:fr})} :
                  {dur>12*60 ? ` Durée excessive (${Math.floor(dur/60)}h)` : ` Durée trop courte (${dur} min)`}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Navigation mois */}
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
        <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(prevMonth(month))}><ChevronLeft size={15}/></button>
        <span style={{fontWeight:600,textTransform:'capitalize'}}>{safeFormat(month+'-15','MMMM yyyy',{locale:fr})}</span>
        <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(nextMonth(month))}><ChevronRight size={15}/></button>
        <button className="btn btn-secondary btn-sm" style={{marginLeft:'auto'}} onClick={exportCSV}>
          ↓ Export CSV
        </button>
      </div>

      {loading ? <div className="empty-state"><span className="spinner"/></div> : (
        Object.entries(byUser).map(([name,data])=>(
          <div key={name} className="card" style={{marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <span style={{fontWeight:700,fontSize:15}}>{name}</span>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{color:'var(--teal)',fontWeight:700}}>
                  {Math.floor(data.totalMin/60)}h{String(data.totalMin%60).padStart(2,'0')} ce mois
                </span>
                <button className="btn btn-secondary btn-sm"
                  onClick={()=>{ setPinSet(entries.find(e=>e.first_name+' '+e.last_name===name)); setNewPin(''); }}
                  style={{fontSize:11}}>
                  🔑 PIN kiosque
                </button>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Entrée</th><th>Sortie</th><th>Durée</th><th>Actions</th></tr></thead>
                <tbody>
                  {data.entries.map(e=>(
                    <tr key={e.id} style={{opacity:e.clock_out?1:0.7}}>
                      <td>{safeFormat(typeof e.work_date==='string'?e.work_date.slice(0,10)+'T12:00:00':e.work_date,'EEE d MMM yyyy',{locale:fr})}</td>
                      <td className="font-mono">{safeFormat(e.clock_in,'HH:mm')}</td>
                      <td className="font-mono">
                        {e.clock_out
                          ? safeFormat(e.clock_out,'HH:mm')
                          : <span className="badge badge-amber" style={{fontSize:10}}>En cours</span>}
                      </td>
                      <td style={{fontWeight:600,color:e.duration_min?'var(--teal)':'var(--amber)'}}>
                        {e.duration_min
                          ? `${Math.floor(e.duration_min/60)}h${String(e.duration_min%60).padStart(2,'0')}`
                          : '—'}
                      </td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          {!e.clock_out && (
                            <button className="btn btn-teal btn-sm" style={{padding:'3px 8px'}}
                              onClick={()=>closeEntry(e.id)}>Clôturer</button>
                          )}
                          <button className="btn btn-ghost btn-sm" style={{padding:'3px 6px'}}
                            onClick={()=>setEdit(e)}><Edit2 size={12}/></button>
                          <button className="btn btn-ghost btn-sm" style={{padding:'3px 6px'}}
                            onClick={()=>setConfirmDeleteEntry(e.id)}><Trash2 size={12} color="var(--red)"/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
      {!loading && Object.keys(byUser).length===0 && <div className="empty-state"><p>Aucun pointage ce mois</p></div>}

      {/* Modal PIN kiosque */}
      {pinSetModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setPinSet(null)}>
          <div className="modal" style={{maxWidth:320,textAlign:'center'}}>
            <h3 className="modal-title">PIN kiosque — {pinSetModal.first_name}</h3>
            <p style={{fontSize:13,color:'var(--text-3)',marginBottom:16}}>
              4 chiffres. Vide = accès sans PIN.
            </p>
            <input type="text" className="input" maxLength={4} pattern="[0-9]*"
              value={newPin} onChange={e=>setNewPin(e.target.value.replace(/\D/g,'').slice(0,4))}
              placeholder="Ex: 1234" style={{textAlign:'center',fontSize:24,letterSpacing:'0.3em',marginBottom:16}}/>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setPinSet(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={async()=>{
                try {
                  await scheduleApi.setKioskPin(pinSetModal.user_id, newPin||null);
                  toast.success('PIN mis à jour');
                  setPinSet(null);
                } catch { toast.error('Erreur'); }
              }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal édition pointage */}
      {editModal && (
        <EditEntryModal entry={editModal} onClose={()=>setEdit(null)} onSaved={()=>{setEdit(null);load();}}/>
      )}

      {confirmDeleteEntry && (
        <ConfirmModal
          title="Supprimer le pointage"
          message="Supprimer ce pointage ?"
          confirmLabel="Supprimer"
          danger
          loading={deletingEntry}
          onConfirm={deleteEntry}
          onCancel={() => setConfirmDeleteEntry(null)}
        />
      )}
    </div>
  );
}

function EditEntryModal({ entry, onClose, onSaved }) {
  const toLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return format(d, "yyyy-MM-dd'T'HH:mm");
  };
  const [clockIn, setClockIn]   = useState(toLocal(entry.clock_in));
  const [clockOut, setClockOut] = useState(toLocal(entry.clock_out));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const toISO = (val) => {
        if (!val) return null;
        try { return new Date(val).toISOString(); } catch { return null; }
      };
      await scheduleApi.updateEntry(entry.id, {
        clockIn:  toISO(clockIn),
        clockOut: toISO(clockOut) || null,
      });
      toast.success('Pointage mis à jour');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <h3 className="modal-title">Modifier le pointage</h3>
        <p className="text-sm text-muted" style={{marginBottom:16}}>
          {entry.first_name} {entry.last_name}
        </p>
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Entrée</label>
            <input type="datetime-local" className="input" value={clockIn}
              onChange={e=>setClockIn(e.target.value)}/>
          </div>
          <div className="form-group">
            <label className="form-label">Sortie <span className="text-muted">(vide = en cours)</span></label>
            <input type="datetime-local" className="input" value={clockOut}
              onChange={e=>setClockOut(e.target.value)}/>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary">Enregistrer</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Mode kiosque ──────────────────────────────────────────────
function KioskMode() {
  const [users, setUsers]         = useState([]);
  const [openEntries, setOpen]    = useState([]);
  const [loading, setLoading]     = useState(true);
  const [confirming, setConfirm]  = useState(null); // {user, action, entryId}

  const [clock, setClock]         = useState('');
  const [pinModal, setPinModal]   = useState(null); // {user, action, entryId}
  const [pinInput, setPinInput]   = useState('');
  const [pinError, setPinError]   = useState(false);

  useEffect(()=>{
    const tick=()=>setClock(new Date().toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit',second:'2-digit'}));
    tick();
    const iv=setInterval(tick,1000);
    return ()=>clearInterval(iv);
  },[]);

  const load = useCallback(async () => {
    try {
      const [u, o] = await Promise.all([
        authApi.users(),
        scheduleApi.kioskStatus(),
      ]);
      setUsers(u.data.users.filter(u => u.is_active && u.role !== 'freelance'));
      setOpen(o.data.openEntries);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUser = (user) => {
    const openEntry = openEntries.find(e => e.user_id === user.id);
    setPinModal({ user, action: openEntry ? 'out' : 'in', entryId: openEntry?.id });
    setPinInput('');
    setPinError(false);
  };

  const submitPin = async () => {
    try {
      const r = await scheduleApi.verifyKioskPin({ userId: pinModal.user.id, pin: pinInput });
      if (!r.data.valid) {
        setPinError(true);
        setTimeout(()=>{ setPinError(false); setPinInput(''); }, 1000);
        return;
      }
      await confirm();
      setPinModal(null);
    } catch { setPinError(true); setTimeout(()=>{ setPinError(false); setPinInput(''); }, 1000); }
  };

  const confirm = async () => {
    const target = pinModal || confirming;
    if (!target) return;
    try {
      if (target.action === 'in') {
        await scheduleApi.kioskClockIn(target.user.id);
        toast.success(`${target.user.first_name} — entrée pointée`);
      } else {
        await scheduleApi.kioskClockOut(target.entryId);
        toast.success(`${target.user.first_name} — sortie pointée`);
      }
      setConfirm(null); setPinModal(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  if (loading) return <div className="empty-state"><span className="spinner"/></div>;

  return (
    <div>
      <div style={{textAlign:'center',marginBottom:24}}>
        <div style={{fontFamily:'monospace',fontSize:56,fontWeight:800,color:'var(--teal)',
          letterSpacing:'0.05em',lineHeight:1,marginBottom:8}}>
          {clock}
        </div>
        <h3 style={{fontSize:16,fontWeight:600,marginBottom:4}}>Pointage rapide</h3>
        <p className="text-muted text-sm">Sélectionnez votre nom puis entrez votre code</p>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12}}>
        {users.map(u => {
          const entry = openEntries.find(e => e.user_id === u.id);
          const isClockedIn = !!entry;
          const dur = entry ? Math.floor((Date.now()-new Date(entry.clock_in).getTime())/60000) : 0;
          return (
            <div key={u.id} onClick={() => handleUser(u)} style={{
              padding:20, borderRadius:14, cursor:'pointer', textAlign:'center',
              background: isClockedIn ? 'rgba(0,210,180,0.1)' : 'var(--bg-elevated)',
              border: `2px solid ${isClockedIn ? 'var(--teal)' : 'var(--border)'}`,
              transition:'all 0.15s',
            }}
            onMouseEnter={e=>e.currentTarget.style.transform='scale(1.03)'}
            onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
              <div style={{width:48,height:48,borderRadius:'50%',margin:'0 auto 10px',
                background: isClockedIn ? 'var(--teal)' : 'linear-gradient(135deg,var(--accent-glow),var(--teal-glow))',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:18,fontWeight:700,color:'#fff',border:'2px solid var(--border)'}}>
                {u.first_name?.[0]}{u.last_name?.[0]}
              </div>
              <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>{u.first_name}</div>
              <div style={{fontSize:12,color:'var(--text-3)',marginBottom:6}}>{u.last_name}</div>
              {isClockedIn ? (
                <div>
                  <div className="badge badge-green" style={{fontSize:10,marginBottom:4}}>En service</div>
                  <div style={{fontSize:11,color:'var(--teal)',fontWeight:600}}>
                    {Math.floor(dur/60)}h{String(dur%60).padStart(2,'0')}
                  </div>
                </div>
              ) : (
                <div className="badge badge-gray" style={{fontSize:10}}>Hors service</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal PIN */}
      {pinModal && (
        <div className="modal-overlay">
          <div className="modal" style={{textAlign:'center',maxWidth:320}}>
            <div style={{fontSize:40,marginBottom:8}}>{pinModal.action==='in'?'👋':'✅'}</div>
            <h3 style={{fontSize:18,fontWeight:700,marginBottom:4}}>
              {pinModal.user.first_name} {pinModal.user.last_name}
            </h3>
            <p style={{color:'var(--text-2)',fontSize:13,marginBottom:16}}>
              {pinModal.action==='in'?'Pointer l\'entrée':'Pointer la sortie'}
            </p>
            <div style={{display:'flex',gap:8,justifyContent:'center',marginBottom:12}}>
              {[1,2,3,4].map(i=>(
                <div key={i} style={{width:36,height:36,borderRadius:'50%',
                  background:pinInput.length>=i?'var(--accent)':'var(--bg-elevated)',
                  border:`2px solid ${pinError?'var(--red)':pinInput.length>=i?'var(--accent)':'var(--border)'}`,
                  transition:'all 0.15s'}}/>
              ))}
            </div>
            {pinError && <div style={{color:'var(--red)',fontSize:12,marginBottom:8}}>Code incorrect</div>}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,maxWidth:200,margin:'0 auto 16px'}}>
              {[1,2,3,4,5,6,7,8,9,'⌫',0,'✓'].map(k=>(
                <button key={String(k)} onClick={()=>{
                  if (k==='⌫') setPinInput(p=>p.slice(0,-1));
                  else if (k==='✓') submitPin();
                  else if (pinInput.length<4) setPinInput(p=>p+String(k));
                }} style={{
                  padding:'14px',borderRadius:10,border:'1px solid var(--border)',
                  background:k==='✓'?'var(--teal)':k==='⌫'?'var(--bg-elevated)':'var(--bg-elevated)',
                  color:k==='✓'?'#fff':'var(--text-1)',
                  fontSize:16,fontWeight:700,cursor:'pointer',fontFamily:'inherit'
                }}>{k}</button>
              ))}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={()=>setPinModal(null)}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}




// ── Mes missions (Freelance) ──────────────────────────────────
function MyMissions({ user }) {
  const [missions, setMissions] = useState([]);
  const [month, setMonth]       = useState(format(new Date(),'yyyy-MM'));
  const [loading, setLoading]   = useState(false);

  useEffect(()=>{
    setLoading(true);
    // Récupérer les réservations où je suis GM ce mois
    api.get('/bookings', {params:{
      gmUserId: user?.id,
      month,
    }}).then(r => setMissions(r.data.bookings||[]))
    .catch(()=>toast.error('Erreur chargement missions'))
    .finally(()=>setLoading(false));
  },[month, user?.id]);

  const totalSessions = missions.length;
  const totalPlayers  = missions.reduce((s,m)=>s+(m.player_count||0),0);

  // Durée estimée = sum(duration_min de chaque salle)
  const exportCSV = () => {
    const header = 'Date,Heure,Salle,Joueurs,Réf,Statut paiement';
    const rows = missions.map(m=>[
      m.session_date?.slice(0,10),
      m.session_start?.slice(0,5),
      m.room_name,
      m.player_count,
      m.booking_ref,
      m.payment_status,
    ].join(','));
    const csv=[header,...rows].join('\n');
    const blob=new Blob([csv],{type:'text/csv'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url;a.download=`missions-${month}.csv`;a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20,flexWrap:'wrap'}}>
        <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(prevMonth(month))}><ChevronLeft size={15}/></button>
        <span style={{fontWeight:600,textTransform:'capitalize'}}>{format(new Date(month+'-15'),'MMMM yyyy',{locale:fr})}</span>
        <button className="btn btn-secondary btn-sm" onClick={()=>setMonth(nextMonth(month))}><ChevronRight size={15}/></button>
        <button className="btn btn-secondary btn-sm" style={{marginLeft:'auto'}} onClick={exportCSV}>
          ↓ Export CSV facturation
        </button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:12,marginBottom:20}}>
        {[
          ['Sessions ce mois', totalSessions, 'var(--accent-light)'],
          ['Joueurs encadrés', totalPlayers, 'var(--teal)'],
        ].map(([label,val,color])=>(
          <div key={label} className="card">
            <div className="text-xs text-muted" style={{marginBottom:4}}>{label}</div>
            <div style={{fontSize:28,fontWeight:700,color}}>{val}</div>
          </div>
        ))}
      </div>

      {loading ? <div className="empty-state"><span className="spinner"/></div> : (
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Heure</th><th>Salle</th>
                  <th>Joueurs</th><th>Référence</th><th>Paiement</th>
                </tr>
              </thead>
              <tbody>
                {missions.length===0 ? (
                  <tr><td colSpan={6} style={{textAlign:'center',padding:30,color:'var(--text-3)'}}>
                    Aucune mission ce mois
                  </td></tr>
                ) : missions.map(m=>(
                  <tr key={m.id}>
                    <td className="bold">{safeFormat(m.session_date?.slice(0,10)+'T12:00:00','EEE d MMM yyyy',{locale:fr})}</td>
                    <td className="font-mono">{m.session_start?.slice(0,5)}</td>
                    <td>{m.room_name}</td>
                    <td style={{textAlign:'center'}}>{m.player_count}</td>
                    <td className="font-mono" style={{fontSize:12,color:'var(--accent-light)'}}>{m.booking_ref}</td>
                    <td>
                      <span className={`badge ${m.payment_status==='paid'?'badge-green':m.payment_status==='partial'?'badge-amber':'badge-red'}`}>
                        {m.payment_status==='paid'?'Soldé':m.payment_status==='partial'?'Acompte':'Impayé'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function SchedulePage() {
  const { can: canPage } = useAuth();
  if (!canPage('schedule.view')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;
  const { user, can } = useAuth();
  const [tab, setTab]          = useState('planning');
  const [sites, setSites]      = useState([]);
  const [absences, setAbsences] = useState([]);
  const [isClockedIn, setIn]   = useState(false);
  const [clockEntry, setEntry] = useState(null);
  const [clockTime, setTime]   = useState(null);
  const [elapsed, setElapsed]  = useState(0);

  // Charger sites
  useEffect(()=>{
    sitesApi.list().then(r=>setSites(r.data.sites)).catch(()=>{});
  },[]);

  // Charger absences du mois pour la grille planning
  useEffect(()=>{
    const month = format(new Date(),'yyyy-MM');
    absencesApi.list({month}).then(r=>{
      const byUser={};
      for (const a of r.data.absences) {
        if (!byUser[a.user_id]) byUser[a.user_id]=[];
        byUser[a.user_id].push(a);
      }
      setAbsences(byUser);
    }).catch(()=>{});
  },[]);

  // Statut pointage persistant
  useEffect(()=>{
    scheduleApi.clockStatus()
      .then(r=>{ setIn(r.data.isClockedIn); setEntry(r.data.entry); setTime(r.data.clockInTime); })
      .catch(()=>{});
  },[]);

  // Timer live
  useEffect(()=>{
    if (!isClockedIn||!clockTime){ setElapsed(0); return; }
    const update=()=>setElapsed(Math.floor((Date.now()-new Date(clockTime).getTime())/1000));
    update();
    const iv=setInterval(update,1000);
    return ()=>clearInterval(iv);
  },[isClockedIn,clockTime]);

  const clockIn = async()=>{
    try {
      const r=await scheduleApi.clockIn();
      setIn(true); setEntry(r.data.entry); setTime(r.data.entry.clock_in);
      toast.success('Pointage entrée enregistré');
    } catch(e){ toast.error(e.response?.data?.error||'Erreur'); }
  };

  const clockOut = async()=>{
    if (!clockEntry) return;
    try {
      await scheduleApi.clockOut(clockEntry.id);
      setIn(false); setEntry(null); setTime(null); setElapsed(0);
      toast.success('Pointage sortie enregistré');
    } catch(e){ toast.error(e.response?.data?.error||'Erreur'); }
  };

  const hh=String(Math.floor(elapsed/3600)).padStart(2,'0');
  const mm=String(Math.floor((elapsed%3600)/60)).padStart(2,'0');
  const ss=String(elapsed%60).padStart(2,'0');

  return (
    <>
      <div className="page-header">
        <div><h2>Planning RH</h2><p>Horaires, absences, templates et pointage</p></div>
        <div style={{display:'flex',gap:8}}>
          {!isClockedIn
            ? <button className="btn btn-teal" onClick={clockIn}><LogIn size={15}/> Pointer l'entrée</button>
            : <button className="btn btn-danger" onClick={clockOut}><LogOut size={15}/> Pointer la sortie</button>
          }
        </div>
      </div>

      <div className="page-body">
        {/* Bandeau pointage */}
        {isClockedIn && (
          <div className="card" style={{borderColor:'var(--teal)',background:'rgba(0,210,180,0.04)',marginBottom:20}}>
            <div style={{display:'flex',alignItems:'center',gap:16}}>
              <div style={{background:'rgba(0,210,180,0.15)',padding:12,borderRadius:10}}>
                <Clock size={20} color="var(--teal)"/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,color:'var(--teal)',fontSize:14}}>En service</div>
                <div style={{fontSize:13,color:'var(--text-2)',marginTop:2}}>
                  Depuis {clockTime?safeFormat(clockTime,'HH:mm'):'—'}
                </div>
              </div>
              <div style={{fontFamily:'monospace',fontSize:28,fontWeight:700,color:'var(--teal)'}}>
                {hh}:{mm}:{ss}
              </div>
              <button className="btn btn-danger btn-sm" onClick={clockOut}>
                <LogOut size={13}/> Pointer la sortie
              </button>
            </div>
          </div>
        )}

        <div className="tabs" style={{flexWrap:'wrap'}}>
          {TABS.filter(([k])=>{
            if (k==='templates' && !can('manager')) return false;
            if (k==='time-entries' && !can('manager')) return false;
            if (k==='kiosk' && !can('manager')) return false;
            if (k==='missions' && user?.role!=='freelance') return false;
            return true;
          }).map(([k,l,Icon])=>(
            <div key={k} className={`tab${tab===k?' active':''}`} onClick={()=>setTab(k)}
              style={{display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
              <Icon size={14}/> <span className="tab-label">{l}</span>
            </div>
          ))}
        </div>

        {tab==='planning'     && <WeekPlanning can={can} user={user} sites={sites} absencesByUser={absences}/>}
        {tab==='absences'     && <AbsencesTab can={can} user={user} sites={sites}/>}
        {tab==='templates'    && <TemplatesTab can={can}/>}
        {tab==='my-hours'     && <MyHours currentUserId={user?.id}/>}
        {tab==='missions'     && <MyMissions user={user}/>}
        {tab==='time-entries' && <AllTimeEntries can={can}/>}
        {tab==='kiosk'        && <KioskMode/>}
      </div>
    </>
  );
}
